import React, { useCallback, useEffect, useState } from "react";
import { platformAPI } from "../services/api";

/**
 * A shop's licensed installs. Freeing one is how an owner locked out with every
 * slot taken (a lost phone, a reset tablet) gets back in: the install stops
 * working at its next request and the slot is free for the device they have now.
 */
export default function ShopDevicesTab({ shop, onChange }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    platformAPI
      .listDevices(shop.id)
      .then(({ data: result }) => setData(result))
      .catch((err) => setError(err.response?.data?.error || "Failed to load devices"));
  }, [shop.id]);

  useEffect(load, [load]);

  const revoke = async (device) => {
    if (!window.confirm(`Free the slot held by "${device.label}"? That install will need activating again.`)) return;
    setBusy(device.id);
    setError("");
    try {
      await platformAPI.revokeDevice(shop.id, device.id);
      load();
      onChange?.();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to free the slot");
    } finally {
      setBusy(null);
    }
  };

  const addSlot = async () => {
    setBusy("limit");
    setError("");
    try {
      await platformAPI.overrideSubscription(shop.id, { device_limit: shop.device_limit + 1 });
      onChange?.();
      load();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to change the limit");
    } finally {
      setBusy(null);
    }
  };

  if (!data) return error ? <p className="text-red-600">{error}</p> : <p className="text-gray-500">Loading…</p>;

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          {data.devices.length} of {data.device_limit} slots in use
        </p>
        {shop.billing_shop_id ? (
          // The limit is the pack's: raised on the shop that pays, for every shop in it
          <span className="text-xs text-gray-500">Raise the limit on {shop.billing_shop_name}: it applies to the whole pack.</span>
        ) : (
          <button
            type="button"
            onClick={addSlot}
            disabled={busy === "limit"}
            className="text-sm font-semibold text-primary-700 hover:underline disabled:opacity-50"
            title="Raises this shop's device limit by one (its plan's limit is restored by assigning the plan again)"
          >
            + 1 slot
          </button>
        )}
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <div className="bg-white rounded-xl shadow divide-y divide-gray-100">
        {data.devices.length === 0 && <p className="p-4 text-sm text-gray-400">No device activated.</p>}
        {data.devices.map((device) => (
          <div key={device.id} className="flex items-center justify-between gap-3 p-4">
            <div className="min-w-0">
              <p className="font-medium text-gray-900 truncate">{device.label}</p>
              <p className="text-xs text-gray-500 truncate">
                Last seen {device.last_seen ? new Date(device.last_seen).toLocaleString() : "never"}
                {device.dormant ? " · dormant" : ""}
                {device.platform ? ` · ${device.platform}` : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={() => revoke(device)}
              disabled={busy === device.id}
              className="shrink-0 text-sm font-semibold text-red-600 hover:underline disabled:opacity-50"
            >
              Free slot
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
